import { defineStore } from "pinia";
import axios from "axios";
import { ProjectApi } from "@/ api/ProjectApi";
import { TagsApi } from "@/ api/TagsApi";
import { ProjectContainer } from "@/models/interfaces/project/project-models";
import { TagEntityType } from "@/models/etc/types";
import { searchStore } from "@/store/search";
import { Contact } from "@/store/client-page";
import { workerStore } from "./worker";
import { AccessApi } from "@/ api/AccessApi";
import { RequestAddressContainer } from "@/composables/useAddressRequest";

export const projectPageStore = defineStore({
    id: 'projectPageStore',
    state: () => ({
        isProjectLoading: true,
        isProjectContactsLoading: true,
        isProjectContactsPartialLoading: false,
        canLoadNextProjectContacts: true,
        accessDenied: false,
        project: null,
        contacts: null,
    } as ProjectPage),
    actions: {
        async getProject(projectId: number) {
            try {
                this.isProjectLoading = true
                this.accessDenied = false
                this.project = await ProjectApi.GetProject(projectId)
                // await this.refreshAccessRules(projectId);
            } catch (error) {
                if (axios.isAxiosError(error) && error.response?.status === 490) {
                    this.accessDenied = true
                    this.project = null
                }
                console.error(error)
            } finally {
                this.isProjectLoading = false
                console.log(this.project)
            }
        },
        async refreshAccessRules(projectId: number) {
              try {
                const result = await AccessApi.getProjectAccessRules(projectId);
                if (!this.project) return;
        
                const worker = workerStore();
                const name = worker.worker?.Name;
        
                const matchedRule = result?.Container.SpecialRules?.find(
                  (rule) => rule.Group?.Name === name,
                ) || '';
                
                if (matchedRule == '') return;
        
                const workerRules = Object.fromEntries(
                  (matchedRule?.Rules ?? []).map((r) => [r.Name, r]),
                );
        
                this.project.NameAccess = workerRules["ProjectName"]?.Value;
                // this.project.NoteAccess = workerRules["ProjectAddress"]?.Value; // спец права не приходят
                this.project.FileAccess = workerRules["ProjectFiles"]?.Value;
                this.project.StageAccess =
                  workerRules["ProjectStage"]?.Value;
                this.project.RuleAccess = workerRules["ProjectRules"]?.Value;
                this.project.JobAccess = workerRules["ProjectJob"]?.Value;
                this.project.PersonAccess = workerRules["ProjectPerson"]?.Value;
                // this.project.AttachedClientsAccess = workerRules["ProjectAttachedClients"]?.Value; // спец права не приходят
                this.project.ParameterAccess = workerRules["ProjectParameters"]?.Value;
                this.project.AccessLevel = workerRules["ProjectAccess"]?.Value;
                this.project.DeleteAccess = workerRules["ProjectDelete"]?.Value;
                this.project.JobPreviousAccess = workerRules["ProjectJobPrevious"]?.Value;
                this.project.FinanceAccess = workerRules["ProjectFinance"]?.Value;

                this.project.AttackedWorkersAccess = workerRules["ProjectAddedWorkers"]?.Value;
                this.project.AttachedClientsAccess = workerRules["ProjectAddedClients"]?.Value;
                  console.log(workerRules);
              } catch (e) {
                console.error(e);
              }
            },
        async getContactsForProject(model: ProjectContactsRequest) {
            type ContactsPage = {
                Count: number,
                Groups: { Id: number, Name: string, Persons: Contact[] }[]
            }

            const isFresh = !model.toSkip
            try {
                if (isFresh) {
                    this.canLoadNextProjectContacts = true
                    this.isProjectContactsLoading = true
                } else {
                    if (!this.canLoadNextProjectContacts) return
                    this.isProjectContactsPartialLoading = true
                }

                const response = await ProjectApi.getContactsForProject(model) as unknown as ContactsPage

                if (isFresh) {
                    this.contacts = response as unknown as Contact[]
                } else {
                    const prev = this.contacts as unknown as ContactsPage | null
                    const prevGroups = prev?.Groups ?? []
                    const byId = new Map(prevGroups.map(g => [g.Id, g]))
                    for (const group of response.Groups ?? []) {
                        const existing = byId.get(group.Id)
                        if (existing) {
                            const existingIds = new Set(existing.Persons.map(p => p.Id))
                            for (const person of group.Persons ?? []) {
                                if (!existingIds.has(person.Id)) existing.Persons.push(person)
                            }
                        } else {
                            prevGroups.push(group)
                            byId.set(group.Id, group)
                        }
                    }
                    this.contacts = {
                        Count: response.Count,
                        Groups: prevGroups
                    } as unknown as Contact[]
                }

                const receivedCount = (response.Groups ?? [])
                    .reduce((acc, g) => acc + (g.Persons?.length ?? 0), 0)
                const pageSize = model.count ?? 0
                if (receivedCount === 0 || (pageSize && receivedCount < pageSize)) {
                    this.canLoadNextProjectContacts = false
                }
            } catch (error) {
                console.error(error);
            } finally {
                this.isProjectContactsLoading = false
                this.isProjectContactsPartialLoading = false
            }
        },
        async renameCurrentProject(name: string) {
            if (!this.project) return

            await ProjectApi.renameProject(this.project.Id, name)
            this.project = { ...this.project, Name: name }
        },
        async deleteProject() {
            if (!this.project) return

            try {
                this.isProjectLoading = true
                await ProjectApi.deleteProject(this.project.Id)
                searchStore().removeProjectFromList(this.project.Id)
            } catch (error) {
                this.isProjectLoading = false
                throw error
            }
        },
        async changeProjectStage(stageId: number) {
            if (!this.project) return

            await ProjectApi.changeStage(this.project.Id, stageId)
            await this.getProject(this.project.Id)
        },
        async attachClientToProject(clientId: number) {
            if (!this.project) return

            await ProjectApi.attachClient(this.project.Id, clientId)
            await this.getProject(this.project.Id)
        },
        async detachClientFromProject(clientId: number) {
            if (!this.project) return

            await ProjectApi.detachClient(this.project.Id, clientId)
            await this.getProject(this.project.Id)
        },
        async attachWorkerToProject(workerId: number) {
            if (!this.project) return

            await ProjectApi.attachWorker(this.project.Id, workerId)
            await this.getProject(this.project.Id)
        },
        async detachWorkerFromProject(workerId: number) {
            if (!this.project) return

            await ProjectApi.detachWorker(this.project.Id, workerId)
            await this.getProject(this.project.Id)
        },
        async attachRoleToWorker(workerId: number, role: { Id: number, Name: string }) {
            if (!this.project) return

            await ProjectApi.attachRole(workerId, this.project.Id, role.Id)
            const worker = this.project.AttachedWorkers?.find((w: any) => w.Id === workerId)
            if (worker) {
                const roles = worker.Roles ?? []
                if (!roles.some((r: any) => r.Id === role.Id)) {
                    worker.Roles = [...roles, { Id: role.Id, Name: role.Name }]
                }
            }
        },
        async detachRoleFromWorker(workerId: number, roleId: number) {
            if (!this.project) return

            await ProjectApi.detachRole(workerId, this.project.Id, roleId)
            const worker = this.project.AttachedWorkers?.find((w: any) => w.Id === workerId)
            if (worker?.Roles) {
                worker.Roles = worker.Roles.filter((r: any) => r.Id !== roleId)
            }
        },
        async attachRoleToClient(clientId: number, role: { Id: number, Name: string }) {
            if (!this.project) return

            await ProjectApi.attachClientRole(clientId, this.project.Id, role.Id)
            const client = this.project.AttachedClients?.find((c: any) => c.Id === clientId)
            if (client) {
                const roles = client.Roles ?? []
                if (!roles.some((r: any) => r.Id === role.Id)) {
                    client.Roles = [...roles, { Id: role.Id, Name: role.Name }]
                }
            }
        },
        async detachRoleFromClient(clientId: number, roleId: number) {
            if (!this.project) return

            await ProjectApi.detachClientRole(clientId, this.project.Id, roleId)
            const client = this.project.AttachedClients?.find((c: any) => c.Id === clientId)
            if (client?.Roles) {
                client.Roles = client.Roles.filter((r: any) => r.Id !== roleId)
            }
        },
        async attachTagToProject(tag: { Name: string }) {
            if (!this.project) return

            const updatedTags = await TagsApi.addTag({
                EditingTagId: 0,
                Name: tag.Name,
                EntityType: TagEntityType.Project,
                EntityId: this.project.Id,
            })
            if (!Array.isArray(updatedTags)) return
            this.project = { ...this.project, Tags: updatedTags as any }
        },
        async detachTagFromProject(tagId: number) {
            if (!this.project) return

            const ok = await TagsApi.deleteTag({
                TagId: tagId,
                EntityType: TagEntityType.Project,
                EntityId: this.project.Id,
            })
            if (!ok) return

            this.project = {
                ...this.project,
                Tags: (this.project.Tags ?? []).filter((t: any) => t.Id !== tagId),
            }
        },
        updateCurrentProjectFavouriteState(newState: boolean) {
            if (this.project) {
                this.project = {
                    ...this.project,
                    Favorite: newState,
                }
            }
        },
    },
    getters: {
        projectLoading(state) {
            return state.isProjectLoading
        },
        openedProject(state) {
            return state.project
        },
        projectContactsLoading(state) {
            return state.isProjectContactsLoading
        },
        projectContactsPartialLoading(state) {
            return state.isProjectContactsPartialLoading
        },
        projectContacts(state) {
            return state.contacts
        },
    },
})

export type ProjectPage = {
    isProjectLoading: boolean,
    isProjectContactsLoading: boolean,
    isProjectContactsPartialLoading: boolean,
    canLoadNextProjectContacts: boolean,
    accessDenied: boolean,
    project: ProjectContainer | null,
    contacts: Contact[] | null,
}

export type ProjectContactsRequest = {
    projectId: number,
    clientId?: number,
    searchType: number,
    useUnion: boolean,
    searchStr: string,
    inputContainer?: RequestAddressContainer | null,
    tags?: number[] | null,
    toSkip: number,
    count: number,
}
